import styles from './index.module.css'
import tabsListStyles from '../tabs-list.module.css'

import * as React from 'react'

import { ALBUMS, SHOWCASES, SIM_SEARCH } from '@disco/constants/FeatureTypes'
import type { SearchType } from '@disco/constants/search'
import {
  getSearchResults,
  updateSearchParams,
} from '@disco/state-management/actions/SearchActions'
import {
  selectBrowseAiMode,
  selectBrowseTypeRestrictions,
  selectIsSearchFiltersInBrowseEnabled,
} from '@disco/state-management/reducers/search-page'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { isSimilarityUrl } from '@disco/utils/search'

import { Tabs, Tooltip } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { Option } from '../select-options'
import { searchTypeOptions } from '../select-options'

// A full sentence per type rather than one interpolated string, so the copy
// stays translatable.
const NON_TRACK_TOOLTIPS: Record<string, string> = {
  '': 'To use the all filter, clear the metadata filter, track tags, or duration filter.',
  playlist:
    'To use the playlist filter, clear the metadata filter, track tags, or duration filter.',
  album:
    'To use the album filter, clear the metadata filter, track tags, or duration filter.',
}

/**
 * Result-type tabs (All / Tracks / Playlists / Albums / Artists) shown in the
 * Browse search pane header, so the type stays switchable after a search runs.
 */
const SearchTypeFilter = (): React.ReactElement => {
  const dispatch = useDispatch()

  const { type: searchType, searchTerm } = useSelector(state => state.search)

  const simSearchEnabled = useFeatureEnabled(SIM_SEARCH)
  const albumsEnabled = useFeatureEnabled(ALBUMS)
  const showcasesEnabled = useFeatureEnabled(SHOWCASES)
  const { disableNonTrackOptions, disableArtistOption } = useSelector(
    selectBrowseTypeRestrictions,
  )
  const isPromptSearch = useSelector(selectBrowseAiMode) === 'active'

  const searchFiltersInBrowseEnabled = useSelector(
    selectIsSearchFiltersInBrowseEnabled,
  )

  // Read off the term, not searchQuery.simSearch, which only lands in the
  // store once the request succeeds.
  const isSimSearch =
    simSearchEnabled &&
    isSimilarityUrl(searchTerm, searchFiltersInBrowseEnabled)

  const options: Option[] = searchTypeOptions
    .filter(option => {
      if (option.value === 'artist') return showcasesEnabled
      if (option.value === 'album') return albumsEnabled
      return true
    })
    .map(option => {
      // Similarity and prompt search only ever return tracks, so lock the menu to Tracks.
      if (isSimSearch || isPromptSearch) {
        return option.value === 'track'
          ? option
          : {
              ...option,
              disabled: true,
              tooltip: isSimSearch
                ? 'Similarity search only works with tracks.'
                : 'Prompt search only works with tracks.',
            }
      }

      switch (option.value) {
        case '':
        case 'playlist':
        case 'album':
          return {
            ...option,
            disabled: disableNonTrackOptions,
            tooltip: disableNonTrackOptions
              ? NON_TRACK_TOOLTIPS[option.value]
              : undefined,
          }
        case 'artist':
          return {
            ...option,
            disabled: disableArtistOption,
            tooltip: disableArtistOption
              ? 'To use the artist filter, clear the other search terms.'
              : undefined,
          }
        default:
          return option
      }
    })

  const handleValueChange = (value: string) => {
    if (options.find(option => option.value === value)?.disabled) return
    const type = (value as SearchType | '') || false
    dispatch(updateSearchParams({ type }))
    dispatch(getSearchResults())
  }

  return (
    <Tabs.Root value={searchType || ''} onValueChange={handleValueChange}>
      <Tabs.List size="1" className={tabsListStyles.tabsList}>
        {options.map(option => {
          const trigger = (
            <Tabs.Trigger
              key={option.value}
              value={option.value}
              // A native disabled button emits no pointer events, so the
              // tooltip explaining the disabled state would never open.
              aria-disabled={option.disabled}
              className={option.disabled ? styles.disabled : undefined}
            >
              {option.label}
            </Tabs.Trigger>
          )
          return option.tooltip ? (
            <Tooltip key={option.value} content={option.tooltip}>
              {trigger}
            </Tooltip>
          ) : (
            trigger
          )
        })}
      </Tabs.List>
    </Tabs.Root>
  )
}

export default SearchTypeFilter
